import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/api/client';
import { useMissatgeError } from '@/api/errors';
import type { MeDto } from '@/api/types';
import { useAuth } from '@/auth/AuthContext';
import { Icona } from '@/components/Icona';
import { useMe } from '@/permisos/MeContext';

const PATRO_MOBIL = /^[67]\d{8}$/;

function normalitzaMobil(valor: string | null | undefined): string {
  let net = (valor ?? '').replace(/[\s\-.()]/g, '');
  if (net.startsWith('+34')) net = net.slice(3);
  else if (net.startsWith('0034')) net = net.slice(4);
  else if (net.startsWith('34') && net.length === 11) net = net.slice(2);
  return net;
}

function useDesaTelefon() {
  const client = useQueryClient();
  const { mode } = useAuth();
  return useMutation({
    mutationFn: async (telefon: string): Promise<MeDto | null> => {
      if (mode === 'mock') return null;
      return apiRequest<MeDto>('/me/telefon', { method: 'PUT', json: { telefon } });
    },
    onSuccess: (desat, telefon) => {
      client.setQueryData<MeDto>(['me'], (actual) =>
        desat ?? (actual ? { ...actual, telefon, telefonConfirmat: true } : actual),
      );
    },
  });
}

function FormTelefon({
  inicial,
  onFet,
  onCancella,
}: {
  inicial: string;
  onFet?: () => void;
  onCancella?: () => void;
}) {
  const { t } = useTranslation('comu');
  const missatgeError = useMissatgeError();
  const desa = useDesaTelefon();
  const [valor, setValor] = useState(inicial);
  const [tocat, setTocat] = useState(false);
  const normalitzat = normalitzaMobil(valor);
  const valid = PATRO_MOBIL.test(normalitzat);

  const envia = () => {
    setTocat(true);
    if (!valid) return;
    desa.mutate(normalitzat, { onSuccess: () => onFet?.() });
  };

  return (
    <form
      className="form"
      onSubmit={(e) => {
        e.preventDefault();
        envia();
      }}
    >
      <div className="camp">
        <label className="camp__label" htmlFor="telefon-mobil">
          {t('telefon.etiqueta')}
        </label>
        <input
          id="telefon-mobil"
          className="input mono"
          type="tel"
          inputMode="tel"
          autoComplete="tel-national"
          autoFocus
          placeholder={t('telefon.placeholder')}
          value={valor}
          aria-invalid={tocat && !valid ? true : undefined}
          onChange={(e) => setValor(e.target.value)}
          onBlur={() => setTocat(true)}
        />
        <span className="camp__ajuda">
          {tocat && !valid ? <span className="c-red">{t('telefon.invalid')}</span> : t('telefon.ajuda')}
        </span>
      </div>
      {desa.error ? (
        <div className="avis-banda" role="alert">
          <Icona nom="exclamationmark-triangle" />
          <span>{missatgeError(desa.error)}</span>
        </div>
      ) : null}
      <div className="modal__peu">
        {onCancella ? (
          <button type="button" className="btn btn--s" onClick={onCancella}>
            {t('telefon.cancella')}
          </button>
        ) : null}
        <button type="submit" className="btn btn--s btn--dark" disabled={desa.isPending}>
          {t('telefon.desa')}
        </button>
      </div>
    </form>
  );
}

function Dialeg({ titol, children }: { titol: string; children: ReactNode }) {
  return (
    <div className="modal-fons">
      <div className="modal" role="dialog" aria-modal="true" aria-label={titol}>
        <div className="panel__head">
          <Icona nom="phone" className="c-blue icon-20" />
          <h3>{titol}</h3>
        </div>
        <div className="panel__body form">{children}</div>
      </div>
    </div>
  );
}

export function OnboardingTelefon() {
  const { t } = useTranslation('comu');
  const { me } = useMe();
  const missatgeError = useMissatgeError();
  const desa = useDesaTelefon();
  const actual = normalitzaMobil(me?.telefon);
  const teValid = PATRO_MOBIL.test(actual);
  const [editant, setEditant] = useState(!teValid);

  return (
    <Dialeg titol={t('telefon.onboardingTitol')}>
      {editant ? (
        <>
          <p>{teValid ? t('telefon.textPerfil') : t('telefon.onboardingSense')}</p>
          <FormTelefon inicial={teValid ? actual : ''} onCancella={teValid ? () => setEditant(false) : undefined} />
        </>
      ) : (
        <>
          <p>{t('telefon.onboardingText')}</p>
          <p className="mono t-title-3">{actual}</p>
          {desa.error ? (
            <div className="avis-banda" role="alert">
              <Icona nom="exclamationmark-triangle" />
              <span>{missatgeError(desa.error)}</span>
            </div>
          ) : null}
          <div className="modal__peu">
            <button type="button" className="btn btn--s" onClick={() => setEditant(true)}>
              {t('telefon.canvia')}
            </button>
            <button
              type="button"
              className="btn btn--s btn--dark"
              disabled={desa.isPending}
              onClick={() => desa.mutate(actual)}
            >
              {t('telefon.esCorrecte')}
            </button>
          </div>
        </>
      )}
    </Dialeg>
  );
}

export function EditaTelefon({ onTanca }: { onTanca: () => void }) {
  const { t } = useTranslation('comu');
  const { me } = useMe();
  const actual = normalitzaMobil(me?.telefon);
  return (
    <Dialeg titol={t('telefon.titolPerfil')}>
      <p>{t('telefon.textPerfil')}</p>
      <FormTelefon inicial={actual} onFet={onTanca} onCancella={onTanca} />
    </Dialeg>
  );
}
